import React from "react"
import { Box, type BoxProps, useMediaQuery, useTheme } from "@mui/material"
import { composeSx } from "../theme/composeSx"
import { NavCollapseControl } from "./NavCollapseControl"

export const SIDEBAR_EXPANDED_WIDTH = 310
export const SIDEBAR_COLLAPSED_WIDTH = 72

export const SIDEBAR_MENU_WIDTH = {
  expanded: SIDEBAR_EXPANDED_WIDTH,
  collapsed: SIDEBAR_COLLAPSED_WIDTH,
} as const

export type SidebarMenuContextValue = {
  collapsed: boolean
  setCollapsed: (collapsed: boolean) => void
  toggleCollapsed: () => void
}

const SidebarMenuContext = React.createContext<SidebarMenuContextValue | null>(
  null,
)

/** Read collapse state from a parent `SidebarMenu`. Defaults to expanded. */
export function useSidebarMenu(): SidebarMenuContextValue {
  return (
    React.useContext(SidebarMenuContext) ?? {
      collapsed: false,
      setCollapsed: () => undefined,
      toggleCollapsed: () => undefined,
    }
  )
}

export type SidebarMenuProps = Omit<BoxProps, "children"> & {
  children: React.ReactNode
  /** Controlled collapse state */
  collapsed?: boolean
  /**
   * Uncontrolled initial collapse state (desktop).
   * On viewports below `md`, when `collapsed` is uncontrolled, the menu is
   * **forced collapsed** regardless of this value — pass `collapsed` to override.
   */
  defaultCollapsed?: boolean
  onCollapsedChange?: (collapsed: boolean) => void
  /**
   * When true, renders `NavCollapseControl` above the list (Storybook / demos).
   * Loop feature chrome (`LoopSidebar`) typically owns the toggle instead.
   */
  showCollapseControl?: boolean
}

/**
 * List container for sidebar navigation items.
 *
 * Supports **expanded** (full labels + section headers) and **collapsed**
 * (narrow icon/initials rail). Items inherit collapse via context.
 *
 * **Mobile:** when `collapsed` is uncontrolled, viewports below `md` force
 * the collapsed rail. Pass `collapsed={false}` to keep labels (e.g. drawer).
 */
export function SidebarMenu({
  children,
  collapsed: collapsedProp,
  defaultCollapsed = false,
  onCollapsedChange,
  showCollapseControl = false,
  sx,
  ...rest
}: SidebarMenuProps) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("md"))
  const [uncontrolled, setUncontrolled] = React.useState(defaultCollapsed)
  const isControlled = collapsedProp !== undefined
  const collapsed = isControlled
    ? Boolean(collapsedProp)
    : isMobile
      ? true
      : uncontrolled

  const setCollapsed = React.useCallback(
    (next: boolean) => {
      if (!isControlled) setUncontrolled(next)
      onCollapsedChange?.(next)
    },
    [isControlled, onCollapsedChange],
  )

  const toggleCollapsed = React.useCallback(() => {
    setCollapsed(!collapsed)
  }, [collapsed, setCollapsed])

  const value = React.useMemo(
    () => ({ collapsed, setCollapsed, toggleCollapsed }),
    [collapsed, setCollapsed, toggleCollapsed],
  )

  return (
    <SidebarMenuContext.Provider value={value}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          width: "100%",
        }}
      >
        {showCollapseControl ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: collapsed ? "center" : "flex-end",
              px: collapsed ? 1 : 1.5,
              pt: 1,
              pb: 0.5,
            }}
          >
            <NavCollapseControl
              collapsed={collapsed}
              onClick={toggleCollapsed}
            />
          </Box>
        ) : null}
        <Box
          component="ul"
          role="list"
          data-collapsed={collapsed ? "true" : "false"}
          sx={composeSx(
            () => ({
              listStyle: "none",
              m: 0,
              p: 0,
              py: 1.5,
              flex: 1,
              width: "100%",
              maxWidth: "100%",
            }),
            sx,
          )}
          {...rest}
        >
          {React.Children.map(children, (child) =>
            child == null ? null : (
              <Box component="li" role="listitem" sx={{ m: 0, p: 0 }}>
                {child}
              </Box>
            ),
          )}
        </Box>
      </Box>
    </SidebarMenuContext.Provider>
  )
}
